import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Workspace } from '@local/client-contracts';
import { WorkspacesService } from './workspaces.service';

export enum WORKSPACE_PERMISSIONS {
  CONNECT_APPS = 'connect_apps',
  ALLOWED_APPS = 'allowed_apps',
  CREATE_API_KEYS = 'create_api_keys',
  ASSISTANT_GENERAL_INTERNAL = 'assistant_general_internal',
  ASSISTANT_GENERAL_EXTERNAL = 'assistant_general_external',
  ASSISTANT_GENERAL = 'general',
  ASSISTANT_AGENTIC = 'agentic',
  ASSISTANT_SLACK = 'slack',
  ASSISTANT_TEAMS = 'teams',
  ASSISTANT_ZENDESK = 'zendesk',
  ASSISTANT_RFP = 'rfp',
  ASSISTANT_SALESFORCE = 'salesforce',
  UNLEASH_AI = 'unleash_ai',
}

export type Role = 'admin' | 'user' | 'owner';
export type WorkspacePermission = {
  isValid?: {
    owner?: (...params) => boolean;
    admin?: (...params) => boolean;
    member?: (...params) => boolean;
  };
  getValue?: () => any;
  setValue?: (value) => Workspace.Workspace;
};

@Injectable({
  providedIn: 'root',
})
export class WorkspacePermissionService {
  private readonly permissions: { [key: string]: WorkspacePermission } = {
    [WORKSPACE_PERMISSIONS.CONNECT_APPS as string]: {
      isValid: { member: () => this.workspace()?.settings?.linking?.state !== 'None' },
      setValue: (value) => {
        const workspace = this.workspace();
        workspace.settings.linking.state = value;
        return workspace;
      },
    },
    [WORKSPACE_PERMISSIONS.ALLOWED_APPS as string]: {
      isValid: { member: (appId) => this.workspace()?.settings?.linking?.allowedApps?.includes(appId) },
      setValue: (value) => {
        const workspace = this.workspace();
        workspace.settings.linking.allowedApps = value;
        return workspace;
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_GENERAL_INTERNAL as string]: {
      isValid: {
        member: () => this.isValidInternalAssistantForMember(),
      },
      setValue: (value) => {
        const workspace = this.workspace();
        workspace.settings.assistant.settings['general'].internalKnowledgeAccess.access = value;
        return workspace;
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_GENERAL_EXTERNAL as string]: {
      isValid: {
        member: () => this.isValidExternalAssistantForMember(),
      },
      setValue: (value) => {
        const workspace = this.workspace();
        workspace.settings.assistant.settings['general'].externalKnowledgeAccess.access = value;
        return workspace;
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_GENERAL as string]: {
      isValid: {
        member: () =>
          this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_GENERAL) &&
          (this.isValidExternalAssistantForMember() || this.isValidInternalAssistantForMember()),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_GENERAL),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_GENERAL),
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_AGENTIC as string]: {
      isValid: {
        member: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_AGENTIC) && this.isValidAgenticAssistantForMember(),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_AGENTIC),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_AGENTIC),
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_SLACK as string]: {
      isValid: {
        member: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_SLACK),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_SLACK),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_SLACK),
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_TEAMS as string]: {
      isValid: {
        member: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_TEAMS),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_TEAMS),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_TEAMS),
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_RFP as string]: {
      isValid: {
        member: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_RFP),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_RFP),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_RFP),
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_ZENDESK as string]: {
      isValid: {
        member: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_ZENDESK),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_ZENDESK),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_ZENDESK),
      },
    },
    [WORKSPACE_PERMISSIONS.ASSISTANT_SALESFORCE as string]: {
      isValid: {
        member: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_SALESFORCE),
        admin: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_SALESFORCE),
        owner: () => this.getEnableAssistant(WORKSPACE_PERMISSIONS.ASSISTANT_SALESFORCE),
      },
    },
    [WORKSPACE_PERMISSIONS.CREATE_API_KEYS as string]: {
      isValid: {
        member: () => this.workspace()?.settings?.api?.creationPermissionLevel !== Workspace.AccessLevel.AdminsOnly,
      },
      getValue: () => this.workspace()?.settings?.api?.creationPermissionLevel || Workspace.AccessLevel.Everyone,
      setValue: (value) => {
        const workspace = this.workspace();
        workspace.settings.api.creationPermissionLevel = value;
        return workspace;
      },
    },
    [WORKSPACE_PERMISSIONS.UNLEASH_AI as string]: {
      isValid: {
        member: () => this.workspace()?.settings?.assistant?.disabledUnleashAI !== true,
        admin: () => this.workspace()?.settings?.assistant?.disabledUnleashAI !== true,
        owner: () => this.workspace()?.settings?.assistant?.disabledUnleashAI !== true,
      },
      getValue: () => this.workspace()?.settings?.assistant?.disabledUnleashAI,
      setValue: (value) => {
        const workspace = this.workspace();
        workspace.settings.assistant.disabledUnleashAI = value;
        return workspace;
      },
    },
  };

  private workspacesService = inject(WorkspacesService);

  private workspace: WritableSignal<Workspace.Workspace> = signal<Workspace.Workspace>(null);
  private isAdmin: WritableSignal<boolean> = signal<boolean>(false);
  private isOwner: WritableSignal<boolean> = signal<boolean>(false);

  constructor() {
    this.workspacesService.current$.subscribe((current) => {
      if (!current) return;

      this.isAdmin.set(current.isAdmin || current.isOwner);
      this.isOwner.set(current.isOwner);
      this.workspace.set(current);
    });
  }

  hasRole(role: Role): boolean {
    switch (role) {
      case 'owner':
        return this.isOwner();
      case 'admin':
        return this.isAdmin();
      case 'user':
        return !this.isAdmin() && !this.isOwner();
    }
  }

  hasWorkspacePermission(type: WORKSPACE_PERMISSIONS, ...params): boolean {
    const isValidByRole = this.permissions[type]?.isValid || {};
    switch (true) {
      case this.isOwner():
        return isValidByRole.owner ? isValidByRole.owner(params) : true;
      case this.isAdmin():
        return isValidByRole.admin ? isValidByRole.admin(params) : true;
      default:
        return isValidByRole.member ? isValidByRole.member(params) : true;
    }
  }

  adminWorkspacePermission(type: WORKSPACE_PERMISSIONS, ...params): boolean {
    const isValidByRole = this.permissions[type]?.isValid || {};
    return isValidByRole.admin ? isValidByRole.admin(params) : true;
  }

  setPermission(type: WORKSPACE_PERMISSIONS, value): Promise<Workspace.Workspace> {
    const setValueFunction = this.permissions[type]?.setValue;
    if (!setValueFunction) {
      return Promise.resolve(this.workspace());
    }
    const workspace = setValueFunction(value);
    return this.workspacesService.update(workspace);
  }

  getValue(type: WORKSPACE_PERMISSIONS): any {
    const getValueFunction = this.permissions[type]?.getValue;
    if (!getValueFunction) {
      return;
    }
    return getValueFunction();
  }

  private isValidExternalAssistantForMember(): boolean {
    const externalAccess = this.workspace()?.settings?.assistant?.settings?.general?.externalKnowledgeAccess?.access;
    return externalAccess !== Workspace.AccessLevel.AdminsOnly;
  }

  private isValidInternalAssistantForMember(): boolean {
    const internalAccess = this.workspace()?.settings?.assistant?.settings?.general?.internalKnowledgeAccess?.access;
    return internalAccess !== Workspace.AccessLevel.AdminsOnly;
  }

  private isValidAgenticAssistantForMember(): boolean {
    const agenticAccess = this.workspace()?.settings?.assistant?.settings?.agentic?.access;
    return agenticAccess !== Workspace.AccessLevel.AdminsOnly;
  }

  private getEnableAssistant(name: string): boolean {
    const workspace = this.workspace();
    return workspace?.settings?.assistant?.settings?.[name]?.enabled ?? true;
  }
}
